import { Button, Text, Stack } from "@mantine/core";
import { IconShieldX } from "@tabler/icons-react";
import { useState } from "react";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { useFreshAuth } from "../hooks/useFreshAuth";
import { useAmplifyAuth } from "./AmplifyAuthContext";
import { FreshAuthRequired } from "./FreshAuthRequired";

interface DisableTOTPButtonProps {
	onSuccess?: () => void;
}

export function DisableTOTPButton({ onSuccess }: DisableTOTPButtonProps) {
	const [isLoading, setIsLoading] = useState(false);
	const { updateMFAPreference, fetchMFAPreference } = useAmplifyAuth();
	const freshAuth = useFreshAuth();

	const handleDisableTOTP = async () => {
		try {
			setIsLoading(true);

			if (!(await freshAuth.ensureFreshAuth())) {
				return;
			}

			// First check if SMS is available
			const currentMFA = await fetchMFAPreference();
			const hasSMS = currentMFA.enabled?.includes("SMS") ?? false;

			if (hasSMS) {
				// Switch to SMS MFA if available
				await updateMFAPreference({ sms: "PREFERRED", totp: "DISABLED" });
				notifications.show({
					title: "Authenticator App Disabled",
					message: "MFA has been switched back to SMS",
					color: "green",
				});
			} else {
				// Disable MFA entirely if SMS not available
				await updateMFAPreference({ sms: "DISABLED", totp: "DISABLED" });
				notifications.show({
					title: "Authenticator App Disabled",
					message:
						"Set up SMS or a new authenticator app to continue using the system.",
					color: "orange",
				});
			}

			onSuccess?.();
		} catch (error) {
			console.error("Failed to disable TOTP:", error);

			// Handle specific error cases
			if (error instanceof Error && error.message.includes("delivery config")) {
				notifications.show({
					title: "SMS MFA Not Available",
					message:
						"Cannot switch to SMS MFA. Please configure a verified phone number first, or contact support.",
					color: "red",
				});
			} else {
				notifications.show({
					title: "Error",
					message:
						error instanceof Error
							? error.message
							: "Failed to disable authenticator app",
					color: "red",
				});
			}
		} finally {
			setIsLoading(false);
		}
	};

	const openConfirmModal = async () => {
		if (!(await freshAuth.ensureFreshAuth())) {
			return;
		}

		try {
			// Check SMS availability before showing modal
			const currentMFA = await fetchMFAPreference();
			const hasSMS = currentMFA.enabled?.includes("SMS") ?? false;

			modals.openConfirmModal({
				title: "Disable Authenticator App",
				children: (
					<Stack gap="sm">
						<Text>Are you sure you want to disable the authenticator app?</Text>
						{hasSMS ? (
							<Text size="sm" c="dimmed">
								Your account will switch back to SMS-based MFA.
							</Text>
						) : (
							<Text size="sm" c="orange">
								Warning: SMS MFA is not configured. You will be required to set
								up SMS or a new authenticator app before you can continue using
								the system.
							</Text>
						)}
					</Stack>
				),
				labels: { confirm: "Disable", cancel: "Cancel" },
				confirmProps: { color: "red" },
				onConfirm: handleDisableTOTP,
			});
		} catch (error) {
			console.error("Failed to check MFA status:", error);
			notifications.show({
				title: "Error",
				message: "Failed to check MFA status",
				color: "red",
			});
		}
	};

	if (freshAuth.needsFreshAuth) {
		return <FreshAuthRequired />;
	}

	return (
		<Button
			variant="light"
			color="red"
			leftSection={<IconShieldX size={16} />}
			onClick={openConfirmModal}
			loading={isLoading}
			size="sm"
		>
			Disable Authenticator App
		</Button>
	);
}
